import React, { useState, useEffect } from "react";
import "./subscribed-students.css";
import { studentAPI } from "../../../utils/api";
import LicenseManager from "../license-manager/license-manager.jsx";

const SubscribedStudents = ({ reloadKey = 0, recentCredentials = [] }) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [showCreds, setShowCreds] = useState(true);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [extendTrialStudent, setExtendTrialStudent] = useState(null);
  const [extendTrialDays, setExtendTrialDays] = useState(7);
  const [extendingId, setExtendingId] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalStudents, setTotalStudents] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  
  // Advanced filters
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [sourceFilter, setSourceFilter] = useState("all"); // all, admin, bixcode
  const [trialFilter, setTrialFilter] = useState("all"); // all, trial, licensed, expired

  // If the user is applying search/filters, we keep the old behavior and fetch
  // everything so client-side filtering remains accurate.
  // Otherwise, we use backend pagination to load only the current page.
  const shouldLoadAll =
    !!searchTerm ||
    statusFilter !== "all" ||
    sourceFilter !== "all" ||
    trialFilter !== "all";
  
  // Bulk selection
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Determine status based on isActive field (simple Active/Inactive)
  const computeStatus = (isActive) => {
    return isActive ? "Active" : "Inactive";
  };

  // Utility: merge passwords from recent credentials into a list of students
  const mergeRecentPasswords = (list) => {
    if (
      !Array.isArray(list) ||
      !Array.isArray(recentCredentials) ||
      recentCredentials.length === 0
    )
      return list;
    const map = new Map();
    for (const c of recentCredentials) {
      if (c && c.email)
        map.set(String(c.email).toLowerCase(), c.password || "");
    }
    return list.map((s) => {
      const emailKey = String(s.email || "").toLowerCase();
      const pwd = map.get(emailKey);
      return pwd ? { ...s, password: pwd } : s;
    });
  };

  // Load full dataset only when the user applies search/filters.
  useEffect(() => {
    if (!shouldLoadAll) return;
    let cancelled = false;

    async function fetchStudentsAll() {
      try {
        setLoading(true);
        setStudents([]);
        setFilteredStudents([]);

        const res = await studentAPI.getStudents({ all: true });
        if (cancelled) return;

        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        const normalized = data.map((s) => {
          const isActive = s.isActive !== false; // Default to true if undefined
          const isTrial = s.isTrialUser || false;
          const trialEnd = s.trialEndAt || s.trialEndsAt;
          const raw =
            s.trialDaysRemaining !== undefined
              ? s.trialDaysRemaining
              : trialEnd
              ? Math.max(
                  0,
                  Math.ceil(
                    (new Date(trialEnd) - new Date()) /
                      (1000 * 60 * 60 * 24)
                  )
                )
              : null;
          const trialDaysRemaining = raw !== null ? Math.min(raw, 7) : null;

          return {
            id: s.id || s._id,
            name: s.name || "",
            email: s.email || "",
            phone: s.phone || "",
            className: s.className || "",
            schoolName: s.schoolName || "",
            registeredDate: s.createdAt || "",
            isActive,
            status: computeStatus(isActive),
            password: s.initialPassword || "",
            licenseKey: s.licenseKey || null,
            registrationSource: s.registrationSource || "admin_panel",
            isTrialUser: isTrial,
            trialDaysRemaining,
            trialEndAt: s.trialEndAt || s.trialEndsAt || null,
            userType: s.userType || null,
            accessLevel: s.accessLevel || null,
          };
        });

        const withPwds = mergeRecentPasswords(normalized);
        setStudents(withPwds);
        setTotalStudents(withPwds.length);
        setCurrentPage(1);
      } catch (e) {
        setStudents([]);
        setFilteredStudents([]);
        setTotalStudents(0);
        setServerTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchStudentsAll();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, shouldLoadAll]);

  // Use backend pagination for the default view (fast).
  useEffect(() => {
    if (shouldLoadAll) return;
    let cancelled = false;

    async function fetchStudentsPage() {
      try {
        setLoading(true);
        setFilteredStudents([]);

        const res = await studentAPI.getStudents({
          page: currentPage,
          limit: itemsPerPage,
        });
        if (cancelled) return;

        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        const normalized = data.map((s) => {
          const isActive = s.isActive !== false; // Default to true if undefined
          const isTrial = s.isTrialUser || false;
          const trialEnd = s.trialEndAt || s.trialEndsAt;
          const raw =
            s.trialDaysRemaining !== undefined
              ? s.trialDaysRemaining
              : trialEnd
              ? Math.max(
                  0,
                  Math.ceil(
                    (new Date(trialEnd) - new Date()) /
                      (1000 * 60 * 60 * 24)
                  )
                )
              : null;
          const trialDaysRemaining = raw !== null ? Math.min(raw, 7) : null;

          return {
            id: s.id || s._id,
            name: s.name || "",
            email: s.email || "",
            phone: s.phone || "",
            className: s.className || "",
            schoolName: s.schoolName || "",
            registeredDate: s.createdAt || "",
            isActive,
            status: computeStatus(isActive),
            password: s.initialPassword || "",
            licenseKey: s.licenseKey || null,
            registrationSource: s.registrationSource || "admin_panel",
            isTrialUser: isTrial,
            trialDaysRemaining,
            trialEndAt: s.trialEndAt || s.trialEndsAt || null,
            userType: s.userType || null,
            accessLevel: s.accessLevel || null,
          };
        });

        const withPwds = mergeRecentPasswords(normalized);
        setStudents(withPwds);

        const pagination = res?.data?.pagination || {};
        setTotalStudents(Number(pagination.totalCount || withPwds.length || 0));
        setServerTotalPages(Number(pagination.totalPages || 1));
        setFilteredStudents(withPwds);
      } catch (e) {
        setStudents([]);
        setFilteredStudents([]);
        setTotalStudents(0);
        setServerTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchStudentsPage();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, shouldLoadAll, currentPage, itemsPerPage]);

  // Overlay recent credentials onto current lists when they change
  useEffect(() => {
    if (!Array.isArray(students) || students.length === 0) return;
    const updated = mergeRecentPasswords(students);
    setStudents(updated);
    setFilteredStudents((prev) => mergeRecentPasswords(prev));
  }, [recentCredentials]);

  // Search and filter functionality with advanced filters
  useEffect(() => {
    let filtered = students;

    // Server-paginated mode: students already represent the current page.
    // We only do client-side sorting (no search/filtering across all 5k+ rows).
    if (!shouldLoadAll) {
      filtered = [...students];
      filtered.sort((a, b) => {
        let aValue = a[sortField] ?? "";
        let bValue = b[sortField] ?? "";

        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortDirection === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
      setFilteredStudents(filtered);
      return;
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((student) => {
        const searchLower = searchTerm.toLowerCase();
        switch (searchField) {
          case "name":
            return (student.name || "").toLowerCase().includes(searchLower);
          case "email":
            return (student.email || "").toLowerCase().includes(searchLower);
          case "phone":
            return (student.phone || "").includes(searchTerm);
          case "className":
            return (student.className || "")
              .toLowerCase()
              .includes(searchLower);
          case "schoolName":
            return (student.schoolName || "")
              .toLowerCase()
              .includes(searchLower);
          default:
            return (
              (student.name || "").toLowerCase().includes(searchLower) ||
              (student.email || "").toLowerCase().includes(searchLower) ||
              (student.phone || "").includes(searchTerm) ||
              (student.className || "").toLowerCase().includes(searchLower) ||
              (student.schoolName || "").toLowerCase().includes(searchLower)
            );
        }
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((student) => {
        if (statusFilter === "active") return student.isActive === true;
        if (statusFilter === "inactive") return student.isActive === false;
        return true;
      });
    }

    // Apply source filter
    if (sourceFilter !== "all") {
      filtered = filtered.filter((student) => {
        if (sourceFilter === "admin") return student.registrationSource === "admin_panel";
        if (sourceFilter === "bixcode") return student.registrationSource === "bixcode_frontend";
        return true;
      });
    }

    // Apply trial filter
    if (trialFilter !== "all") {
      filtered = filtered.filter((student) => {
        if (trialFilter === "trial") {
          return student.isTrialUser && student.trialDaysRemaining > 0;
        }
        if (trialFilter === "expired") {
          return student.userType === "trial_expired" || 
                 (student.isTrialUser && student.trialDaysRemaining <= 0);
        }
        if (trialFilter === "licensed") {
          return student.licenseKey && !student.isTrialUser;
        }
        return true;
      });
    }

    // Sort functionality
    filtered.sort((a, b) => {
      let aValue = a[sortField] ?? "";
      let bValue = b[sortField] ?? "";

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredStudents(filtered);
    setCurrentPage(1); // Reset to first page when filters change
    setSelectedStudents(new Set()); // Clear selection when filters change
    setSelectAll(false);
  }, [
    students,
    shouldLoadAll,
    searchTerm,
    searchField,
    sortField,
    sortDirection,
    statusFilter,
    sourceFilter,
    trialFilter,
  ]);

  // Get paginated students for display
  const getPaginatedStudents = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredStudents.slice(startIndex, endIndex);
  };

  const totalPagesClient = Math.ceil(filteredStudents.length / itemsPerPage);
  const totalPages = shouldLoadAll ? totalPagesClient : serverTotalPages;
  const paginatedStudents = shouldLoadAll ? getPaginatedStudents() : filteredStudents;

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedStudents(new Set()); // Clear selection when changing pages
      setSelectAll(false);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setSelectedStudents(new Set());
    setSelectAll(false);
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents(new Set());
    } else {
      const allIds = new Set(paginatedStudents.map(s => s.id));
      setSelectedStudents(allIds);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectStudent = (id) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedStudents(newSelected);
    setSelectAll(newSelected.size === paginatedStudents.length);
  };

  // Export selected students
  const handleExportSelected = (selectedStudentsList) => {
    try {
      if (!selectedStudentsList || selectedStudentsList.length === 0) {
        alert("No students selected for export.");
        return;
      }

      // Generate CSV for selected students
      const csvContent = [
        [
          "S.NO",
          "Name",
          "Email",
          "Password",
          "Phone",
          "Class",
          "School",
          "Registered Date",
          "Source",
          "Trial Days",
          "Status",
          "License Key",
        ],
        ...selectedStudentsList.map((student, index) => [
          index + 1,
          `"${(student.name || "").replace(/"/g, '""')}"`,
          student.email,
          student.password || "",
          student.phone || "",
          `"${(student.className || "").replace(/"/g, '""')}"`,
          `"${(student.schoolName || "").replace(/"/g, '""')}"`,
          student.registeredDate
            ? new Date(student.registeredDate).toLocaleDateString()
            : "",
          student.registrationSource === "bixcode_frontend" ? "BixCode" : "Admin",
          student.isTrialUser
            ? student.trialDaysRemaining !== null
              ? student.trialDaysRemaining > 0
                ? `${student.trialDaysRemaining} days`
                : "Expired"
              : "N/A"
            : "-",
          student.status,
          student.licenseKey || "",
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `selected_students_${selectedStudentsList.length}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      alert(`✅ Successfully exported ${selectedStudentsList.length} selected student(s)!`);
    } catch (error) {
      console.error("Export failed:", error);
      alert("❌ Failed to export selected students. Please try again.");
    }
  };

  // Export filtered students
  const handleExport = async (exportAll = false) => {
    try {
      setLoading(true);
      
      // Determine what to export
      let studentsToExport;
      let filename;
      
      if (exportAll) {
        // Export all students from database
        const res = await studentAPI.getStudents({ all: true });
        const allStudentsData = Array.isArray(res?.data?.data) ? res.data.data : [];
        
        const normalized = allStudentsData.map((s) => {
          const isActive = s.isActive !== false;
          const isTrial = s.isTrialUser || false;
          const trialEnd = s.trialEndAt || s.trialEndsAt;
          const raw =
            s.trialDaysRemaining !== undefined
              ? s.trialDaysRemaining
              : trialEnd
              ? Math.max(
                  0,
                  Math.ceil(
                    (new Date(trialEnd) - new Date()) /
                      (1000 * 60 * 60 * 24)
                  )
                )
              : null;
          const trialDaysRemaining = raw !== null ? Math.min(raw, 7) : null;

          return {
            id: s.id || s._id,
            name: s.name || "",
            email: s.email || "",
            phone: s.phone || "",
            className: s.className || "",
            schoolName: s.schoolName || "",
            registeredDate: s.createdAt || "",
            isActive: isActive,
            status: computeStatus(isActive),
            password: s.initialPassword || "",
            licenseKey: s.licenseKey || null,
            registrationSource: s.registrationSource || "admin_panel",
            isTrialUser: isTrial,
            trialDaysRemaining: trialDaysRemaining,
            trialEndAt: s.trialEndAt || s.trialEndsAt || null,
            userType: s.userType || null,
            accessLevel: s.accessLevel || null,
          };
        });
        
        studentsToExport = mergeRecentPasswords(normalized);
        filename = `all_students_${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        // Export filtered students
        studentsToExport = filteredStudents;
        const filterDesc = searchTerm ? `filtered_${searchTerm}` : 'filtered';
        filename = `students_${filterDesc}_${new Date().toISOString().split('T')[0]}.csv`;
      }
      
      // Generate CSV
      const csvContent = [
        [
          "S.NO",
          "Name",
          "Email",
          "Password",
          "Phone",
          "Class",
          "School",
          "Registered Date",
          "Source",
          "Trial Days",
          "Status",
          "License Key",
        ],
        ...studentsToExport.map((student, index) => [
          index + 1,
          `"${(student.name || "").replace(/"/g, '""')}"`,
          student.email,
          student.password || "",
          student.phone || "",
          `"${(student.className || "").replace(/"/g, '""')}"`,
          `"${(student.schoolName || "").replace(/"/g, '""')}"`,
          student.registeredDate
            ? new Date(student.registeredDate).toLocaleDateString()
            : "",
          student.registrationSource === "bixcode_frontend" ? "BixCode" : "Admin",
          student.isTrialUser
            ? student.trialDaysRemaining !== null
              ? student.trialDaysRemaining > 0
                ? `${student.trialDaysRemaining} days`
                : "Expired"
              : "N/A"
            : "-",
          student.status,
          student.licenseKey || "",
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      alert(`✅ Successfully exported ${studentsToExport.length} student(s)!`);
    } catch (error) {
      console.error("Export failed:", error);
      alert("❌ Failed to export students. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    const confirmed = window.confirm("Remove this student?");
    if (!confirmed) return;
    try {
      setDeletingId(id);
      await studentAPI.deleteStudent(id);
      // Remove from both lists
      const nextStudents = students.filter((s) => s.id !== id);
      setStudents(nextStudents);
      setFilteredStudents((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      // Silent failure - user will see student remains in list
    } finally {
      setDeletingId(null);
    }
  };

  const handleExtendTrialConfirm = async () => {
    if (!extendTrialStudent || !extendTrialStudent.id) return;
    const days = Math.min(Math.max(parseInt(extendTrialDays, 10) || 7, 1), 365);
    try {
      setExtendingId(extendTrialStudent.id);
      const res = await studentAPI.extendTrial(extendTrialStudent.id, {
        extendByDays: days,
      });

      const newEndString =
        res?.data?.trialEndsAt || res?.data?.trialEndAt || null;

      const updateListWithNewTrial = (list) =>
        list.map((s) => {
          if (s.id !== extendTrialStudent.id) return s;
          const trialEnd =
            newEndString || s.trialEndAt || s.trialEndsAt || null;
          const raw =
            trialEnd !== null
              ? Math.max(
                  0,
                  Math.ceil(
                    (new Date(trialEnd) - new Date()) /
                      (1000 * 60 * 60 * 24)
                  )
                )
              : null;
          const trialDaysRemaining =
            raw !== null ? Math.min(raw, 7) : null;

          return {
            ...s,
            isTrialUser: true,
            trialEndAt: trialEnd,
            trialDaysRemaining,
            userType: s.userType || "registered",
            accessLevel: s.accessLevel || "trial",
          };
        });

      setStudents((prev) => updateListWithNewTrial(prev));
      setFilteredStudents((prev) => updateListWithNewTrial(prev));

      setExtendTrialStudent(null);
      setExtendTrialDays(7);
      alert(`Trial extended by ${days} days for ${extendTrialStudent.name}.`);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          "Failed to extend trial. Please try again."
      );
    } finally {
      setExtendingId(null);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    if (!id) return;
    const newStatus = !currentStatus;
    const action = newStatus ? "activate" : "deactivate";
    const confirmed = window.confirm(
      `Are you sure you want to ${action} this student?`
    );
    if (!confirmed) return;

    try {
      setTogglingId(id);
      await studentAPI.updateStudent(id, { isActive: newStatus });

      // Update local state
      const updatedStudents = students.map((s) =>
        s.id === id
          ? { ...s, isActive: newStatus, status: computeStatus(newStatus) }
          : s
      );
      setStudents(updatedStudents);
      setFilteredStudents((prev) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, isActive: newStatus, status: computeStatus(newStatus) }
            : s
        )
      );
    } catch (e) {
      alert("Failed to update student status. Please try again.");
    } finally {
      setTogglingId(null);
    }
  };

  // Handle bulk actions (activate/deactivate/delete) on selected or filtered students
  const handleBulkAction = async (action, useSelected = false) => {
    let targetStudents;
    let count;
    
    if (useSelected && selectedStudents.size > 0) {
      // Use selected students
      targetStudents = Array.from(selectedStudents);
      count = targetStudents.length;
    } else {
      // Use all filtered students
      targetStudents = filteredStudents.map(s => s.id);
      count = filteredStudents.length;
    }

    if (count === 0) {
      alert("No students selected or matching filters");
      return;
    }

    const actionText =
      action === "activate"
        ? "enable student access for"
        : action === "deactivate"
        ? "disable student access for"
        : "delete student records for";

    const targetDescription = useSelected && selectedStudents.size > 0
      ? `${count} selected student${count !== 1 ? "s" : ""}`
      : searchTerm
      ? `${count} student${count !== 1 ? "s" : ""} matching current filters`
      : `all ${count} student${count !== 1 ? "s" : ""}`;

    const confirmed = window.confirm(
      `⚠️ This will ${actionText} ${targetDescription}.\n\nContinue?`
    );

    if (!confirmed) return;

    // Check if user is still logged in
    const isLoggedIn = localStorage.getItem("adminLoggedIn") === "true";
    if (!isLoggedIn) {
      alert("❌ Session expired. Please login again.");
      window.location.reload();
      return;
    }

    try {
      setBulkActionLoading(true);

      const response = await studentAPI.bulkUpdateStudents({
        studentIds: targetStudents,
        action: action,
      });

      // Refresh the list
      if (action === "delete") {
        const remainingStudents = students.filter(
          (s) => !targetStudents.includes(s.id)
        );
        setStudents(remainingStudents);
        setTotalStudents(remainingStudents.length);
      } else {
        // Update status for activate/deactivate actions
        const isActive = action === "activate";

        // Update the main students list
        const updatedStudents = students.map((s) =>
          targetStudents.includes(s.id)
            ? { ...s, isActive, status: computeStatus(isActive) }
            : s
        );
        setStudents(updatedStudents);
      }

      setShowBulkMenu(false);
      setSelectedStudents(new Set());
      setSelectAll(false);

      const successText =
        action === "activate"
          ? "enabled student access for"
          : action === "deactivate"
          ? "disabled student access for"
          : "deleted student records for";
      alert(
        `✅ Successfully ${successText} ${count} student${
          count !== 1 ? "s" : ""
        }!`
      );
    } catch (error) {
      console.error("Bulk action failed:", error);

      // Check if it's an authentication error
      if (error.response && error.response.status === 401) {
        alert("❌ Session expired. Please login again.");
        // Redirect to login by clearing localStorage
        localStorage.removeItem("adminLoggedIn");
        localStorage.removeItem("adminLoginTime");
        window.location.reload();
        return;
      }

      // Check if it's a network error
      if (error.code === "NETWORK_ERROR" || !error.response) {
        alert(
          "❌ Network error. Please check if the backend server is running."
        );
        return;
      }

      // Generic error
      alert(
        `❌ Failed to ${
          action === "delete" ? "delete student records for" : actionText
        } students. Please try again.`
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showBulkMenu && !e.target.closest(".bulk-actions-dropdown")) {
        setShowBulkMenu(false);
      }
      
      const exportMenu = document.getElementById('export-menu');
      if (exportMenu && exportMenu.classList.contains('show') && !e.target.closest('.export-button') && !e.target.closest('#export-menu')) {
        exportMenu.classList.remove('show');
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showBulkMenu]);

  // Static table headers rendered in a readable, maintainable way
  const tableHeaders = [
    <input 
      type="checkbox" 
      checked={selectAll}
      onChange={handleSelectAll}
      style={{ cursor: "pointer", width: "18px", height: "18px" }}
      title="Select all on this page"
    />,
    "S.NO",
    "Name",
    "Email",
    "Password",
    "Phone",
    "Class",
    "School Name",
    "Registered",
    "Source",
    "Trial Days",
    "Status",
    "License Key",
    "Actions",
  ];

  return (
    <div className="subscribed-students">
      {Array.isArray(recentCredentials) &&
      recentCredentials.length > 0 &&
      showCreds ? (
        <div
          className="credentials-panel"
          style={{
            marginBottom: 16,
            border: "1px solid #c8e6c9",
            background: "#e8f5e9",
            padding: 12,
            borderRadius: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <strong>Recently generated credentials</strong>
            <button
              onClick={() => setShowCreds(false)}
              className="clear-button"
              style={{ padding: "4px 8px" }}
            >
              Hide
            </button>
          </div>
          <div style={{ marginTop: 8, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>
                    Email
                  </th>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>
                    Password
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentCredentials.map((c, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: "6px 8px" }}>{c.email}</td>
                    <td style={{ padding: "6px 8px", fontFamily: "monospace" }}>
                      {c.password}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      <div className="students-header">
        <h2>Subscribed Students Management</h2>
        <div className="header-actions">
          {/* Bulk Actions Dropdown */}
          <div
            className="bulk-actions-dropdown"
            style={{ position: "relative", display: "inline-block" }}
          >
            <button
              className="bulk-actions-button"
              onClick={(e) => {
                e.stopPropagation();
                setShowBulkMenu(!showBulkMenu);
              }}
              disabled={bulkActionLoading}
              style={{
                padding: "0.75rem 1.5rem",
                backgroundColor:
                  filteredStudents.length < students.length
                    ? "#2196f3"
                    : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: bulkActionLoading ? "not-allowed" : "pointer",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.95rem",
                transition: "all 0.3s ease",
                opacity: bulkActionLoading ? 0.6 : 1,
              }}
              title={
                filteredStudents.length < students.length
                  ? `Manage ${filteredStudents.length} filtered students`
                  : `Manage all ${students.length} students`
              }
            >
              Bulk Actions {showBulkMenu ? "▲" : "▼"}
              {filteredStudents.length < students.length && (
                <span
                  style={{
                    backgroundColor: "white",
                    color: "#2196f3",
                    padding: "0.2rem 0.5rem",
                    borderRadius: "12px",
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    minWidth: "24px",
                    textAlign: "center",
                  }}
                >
                  {filteredStudents.length}
                </span>
              )}
            </button>

            {/* Dropdown Menu */}
            {showBulkMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: "0.5rem",
                  backgroundColor: "white",
                  border: "2px solid #2196f3",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  minWidth: "320px",
                  zIndex: 1000,
                }}
              >
                {/* Info Header */}
                <div
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "#e3f2fd",
                    borderBottom: "1px solid #2196f3",
                    fontSize: "0.9rem",
                  }}
                >
                  {selectedStudents.size > 0 ? (
                    <>
                      <strong style={{ color: "#1976d2" }}>
                        ✓ Selected:
                      </strong>{" "}
                      {selectedStudents.size} student{selectedStudents.size !== 1 ? "s" : ""}
                    </>
                  ) : (
                    <>
                      <strong style={{ color: "#1976d2" }}>
                        Filtered Students:
                      </strong>{" "}
                      {filteredStudents.length} student
                      {filteredStudents.length !== 1 ? "s" : ""}
                      {(searchTerm || statusFilter !== "all" || sourceFilter !== "all" || trialFilter !== "all") && (
                        <div
                          style={{
                            fontSize: "0.8rem",
                            color: "#555",
                            marginTop: "0.25rem",
                          }}
                        >
                          Active filters applied
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Actions */}
                <div style={{ padding: "0.5rem" }}>
                  {selectedStudents.size > 0 && (
                    <>
                      <div style={{ padding: "0.5rem 0.75rem", fontSize: "0.85rem", color: "#666", borderBottom: "1px solid #eee" }}>
                        Actions on Selected ({selectedStudents.size})
                      </div>
                      <button
                        onClick={() => {
                          const selected = filteredStudents.filter(s => selectedStudents.has(s.id));
                          handleExportSelected(selected);
                          setShowBulkMenu(false);
                        }}
                        disabled={bulkActionLoading}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          backgroundColor: "transparent",
                          border: "none",
                          textAlign: "left",
                          cursor: bulkActionLoading ? "not-allowed" : "pointer",
                          borderRadius: "4px",
                          fontSize: "0.95rem",
                          transition: "background 0.2s",
                          fontWeight: "500",
                          color: "#4CAF50"
                        }}
                        onMouseEnter={(e) =>
                          !bulkActionLoading &&
                          (e.target.style.backgroundColor = "#e8f5e9")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        📊 Export Selected CSV ({selectedStudents.size})
                      </button>
                      <button
                        onClick={() => handleBulkAction("activate", true)}
                        disabled={bulkActionLoading}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          backgroundColor: "transparent",
                          border: "none",
                          textAlign: "left",
                          cursor: bulkActionLoading ? "not-allowed" : "pointer",
                          borderRadius: "4px",
                          fontSize: "0.95rem",
                          transition: "background 0.2s",
                          fontWeight: "500",
                        }}
                        onMouseEnter={(e) =>
                          !bulkActionLoading &&
                          (e.target.style.backgroundColor = "#e8f5e9")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        ▶ Enable Selected ({selectedStudents.size})
                      </button>
                      <button
                        onClick={() => handleBulkAction("deactivate", true)}
                        disabled={bulkActionLoading}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          backgroundColor: "transparent",
                          border: "none",
                          textAlign: "left",
                          cursor: bulkActionLoading ? "not-allowed" : "pointer",
                          borderRadius: "4px",
                          fontSize: "0.95rem",
                          transition: "background 0.2s",
                          fontWeight: "500",
                        }}
                        onMouseEnter={(e) =>
                          !bulkActionLoading &&
                          (e.target.style.backgroundColor = "#fff3e0")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        ⏸ Disable Selected ({selectedStudents.size})
                      </button>
                      <button
                        onClick={() => handleBulkAction("delete", true)}
                        disabled={bulkActionLoading}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          backgroundColor: "transparent",
                          border: "none",
                          textAlign: "left",
                          cursor: bulkActionLoading ? "not-allowed" : "pointer",
                          borderRadius: "4px",
                          fontSize: "0.95rem",
                          color: "#d32f2f",
                          transition: "background 0.2s",
                          fontWeight: "500",
                        }}
                        onMouseEnter={(e) =>
                          !bulkActionLoading &&
                          (e.target.style.backgroundColor = "#ffebee")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        🗑 Delete Selected ({selectedStudents.size})
                      </button>
                      <div style={{ padding: "0.5rem 0.75rem", fontSize: "0.85rem", color: "#666", borderTop: "1px solid #eee", borderBottom: "1px solid #eee", marginTop: "0.5rem" }}>
                        Actions on All Filtered ({filteredStudents.length})
                      </div>
                    </>
                  )}
                  <button
                    onClick={() => {
                      handleExport(false);
                      setShowBulkMenu(false);
                    }}
                    disabled={bulkActionLoading}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      backgroundColor: "transparent",
                      border: "none",
                      textAlign: "left",
                      cursor: bulkActionLoading ? "not-allowed" : "pointer",
                      borderRadius: "4px",
                      fontSize: "0.95rem",
                      transition: "background 0.2s",
                      fontWeight: "500",
                      color: "#4CAF50"
                    }}
                    onMouseEnter={(e) =>
                      !bulkActionLoading &&
                      (e.target.style.backgroundColor = "#e8f5e9")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    📊 Export Filtered CSV ({filteredStudents.length})
                  </button>
                  <button
                    onClick={() => handleBulkAction("activate", false)}
                    disabled={bulkActionLoading}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      backgroundColor: "transparent",
                      border: "none",
                      textAlign: "left",
                      cursor: bulkActionLoading ? "not-allowed" : "pointer",
                      borderRadius: "4px",
                      fontSize: "0.95rem",
                      transition: "background 0.2s",
                      fontWeight: "500",
                    }}
                    onMouseEnter={(e) =>
                      !bulkActionLoading &&
                      (e.target.style.backgroundColor = "#e8f5e9")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    ▶ Enable All Filtered ({filteredStudents.length})
                  </button>

                  <button
                    onClick={() => handleBulkAction("deactivate", false)}
                    disabled={bulkActionLoading}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      backgroundColor: "transparent",
                      border: "none",
                      textAlign: "left",
                      cursor: bulkActionLoading ? "not-allowed" : "pointer",
                      borderRadius: "4px",
                      fontSize: "0.95rem",
                      transition: "background 0.2s",
                      fontWeight: "500",
                    }}
                    onMouseEnter={(e) =>
                      !bulkActionLoading &&
                      (e.target.style.backgroundColor = "#fff3e0")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    ⏸ Disable All Filtered ({filteredStudents.length})
                  </button>

                  <button
                    onClick={() => handleBulkAction("delete", false)}
                    disabled={bulkActionLoading}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      backgroundColor: "transparent",
                      border: "none",
                      textAlign: "left",
                      cursor: bulkActionLoading ? "not-allowed" : "pointer",
                      borderRadius: "4px",
                      fontSize: "0.95rem",
                      color: "#d32f2f",
                      transition: "background 0.2s",
                      fontWeight: "500",
                    }}
                    onMouseEnter={(e) =>
                      !bulkActionLoading &&
                      (e.target.style.backgroundColor = "#ffebee")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    🗑 Delete All Filtered ({filteredStudents.length})
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Export Dropdown */}
          <div style={{ position: "relative", display: "inline-block" }}>
            <button 
              className="export-button"
              onClick={() => document.getElementById('export-menu').classList.toggle('show')}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              Export CSV ▼
            </button>
            <div 
              id="export-menu"
              style={{
                display: "none",
                position: "absolute",
                right: 0,
                marginTop: "0.5rem",
                backgroundColor: "white",
                border: "2px solid #4CAF50",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                minWidth: "220px",
                zIndex: 1000,
              }}
              className="export-dropdown"
            >
              <button
                onClick={() => {
                  handleExport(false);
                  document.getElementById('export-menu').classList.remove('show');
                }}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  backgroundColor: "transparent",
                  border: "none",
                  textAlign: "left",
                  cursor: "pointer",
                  borderRadius: "4px",
                  fontSize: "0.95rem",
                  transition: "background 0.2s",
                  fontWeight: "500",
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#e8f5e9"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                📊 Export Filtered ({filteredStudents.length})
              </button>
              <button
                onClick={() => {
                  handleExport(true);
                  document.getElementById('export-menu').classList.remove('show');
                }}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  backgroundColor: "transparent",
                  border: "none",
                  textAlign: "left",
                  cursor: "pointer",
                  borderRadius: "4px",
                  fontSize: "0.95rem",
                  transition: "background 0.2s",
                  fontWeight: "500",
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#e8f5e9"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                📁 Export All ({totalStudents})
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-section">
        <div className="search-container">
          <div className="search-input-group">
            <select
              className="search-field-select"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
            >
              <option value="all">All Fields</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="className">Class Name</option>
              <option value="schoolName">School Name</option>
            </select>
            <input
              type="text"
              className="search-input"
              placeholder={`Search by ${
                searchField === "all" ? "all fields" : searchField
              }...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="search-clear" onClick={() => setSearchTerm("")}>
              ✕
            </button>
          </div>
          <div className="search-stats">
            {loading
              ? "Searching..."
              : searchTerm || statusFilter !== "all" || sourceFilter !== "all" || trialFilter !== "all"
              ? `Showing ${filteredStudents.length} of ${totalStudents} students`
              : `Total: ${totalStudents} students`}
          </div>
        </div>
        
        {/* Advanced Filters */}
        <div style={{ 
          display: "flex", 
          gap: "1rem", 
          marginTop: "1rem", 
          flexWrap: "wrap",
          alignItems: "center"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <label style={{ fontSize: "0.9rem", fontWeight: "500", color: "#555" }}>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                cursor: "pointer",
                backgroundColor: statusFilter !== "all" ? "#e3f2fd" : "white"
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <label style={{ fontSize: "0.9rem", fontWeight: "500", color: "#555" }}>Source:</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                cursor: "pointer",
                backgroundColor: sourceFilter !== "all" ? "#e3f2fd" : "white"
              }}
            >
              <option value="all">All Sources</option>
              <option value="admin">Admin Panel</option>
              <option value="bixcode">BixCode</option>
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <label style={{ fontSize: "0.9rem", fontWeight: "500", color: "#555" }}>Type:</label>
            <select
              value={trialFilter}
              onChange={(e) => setTrialFilter(e.target.value)}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                cursor: "pointer",
                backgroundColor: trialFilter !== "all" ? "#e3f2fd" : "white"
              }}
            >
              <option value="all">All Types</option>
              <option value="trial">Active Trial</option>
              <option value="expired">Expired Trial</option>
              <option value="licensed">Licensed</option>
            </select>
          </div>

          {(searchTerm || statusFilter !== "all" || sourceFilter !== "all" || trialFilter !== "all") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setSourceFilter("all");
                setTrialFilter("all");
              }}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                border: "1px solid #f44336",
                backgroundColor: "#ffebee",
                color: "#d32f2f",
                fontSize: "0.9rem",
                cursor: "pointer",
                fontWeight: "500",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f44336";
                e.target.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#ffebee";
                e.target.style.color = "#d32f2f";
              }}
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      <div className="students-table-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading students...</p>
          </div>
        ) : (
          <table className="students-table">
            <thead
              onMouseOver={(e) => {
                if (e.target && e.target.tagName === "TH") {
                  e.target.style.backgroundColor = "inherit";
                }
              }}
              onMouseOut={(e) => {
                if (e.target && e.target.tagName === "TH") {
                  e.target.style.backgroundColor = "";
                }
              }}
            >
              <tr>
                {tableHeaders.map((label) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedStudents.length === 0 ? (
                <tr>
                  <td colSpan="14" className="no-data">
                    {searchTerm || statusFilter !== "all" || sourceFilter !== "all" || trialFilter !== "all"
                      ? "No students found matching your filters."
                      : "No students found."}
                  </td>
                </tr>
              ) : (
                paginatedStudents.map((student, index) => (
                  <tr key={student.id}>
                    <td>
                      <input 
                        type="checkbox"
                        checked={selectedStudents.has(student.id)}
                        onChange={() => handleSelectStudent(student.id)}
                        style={{ cursor: "pointer", width: "18px", height: "18px" }}
                      />
                    </td>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>
                      {student.name}
                      {student.userType === "trial_expired" ? (
                        <span className="trial-expired-badge">Trial Expired</span>
                      ) : student.isTrialUser ? (
                        <span className="trial-badge">TRIAL</span>
                      ) : null}
                    </td>
                    <td>{student.email}</td>
                    <td>{student.password ? student.password : "-"}</td>
                    <td>{student.phone || "-"}</td>
                    <td>{student.className || "-"}</td>
                    <td>{student.schoolName || "-"}</td>
                    <td>
                      {student.registeredDate
                        ? new Date(student.registeredDate).toLocaleDateString(
                            undefined,
                            { day: "2-digit", month: "short", year: "numeric" }
                          )
                        : "-"}
                    </td>
                    <td>
                      <span
                        className={`source-badge ${
                          student.registrationSource === "bixcode_frontend"
                            ? "bixcode"
                            : "admin"
                        }`}
                      >
                        {student.registrationSource === "bixcode_frontend"
                          ? "BixCode"
                          : "Admin"}
                      </span>
                    </td>
                    <td>
                      {student.userType === "trial_expired" ? (
                        <span className="trial-days expired">Expired</span>
                      ) : student.isTrialUser ? (
                        <span
                          className={`trial-days ${
                          student.trialDaysRemaining <= 0
                            ? "expired"
                            : student.trialDaysRemaining <= 2
                            ? "warning"
                            : "active"
                        }`}
                        >
                          {student.trialDaysRemaining !== null
                            ? student.trialDaysRemaining > 0
                              ? `${student.trialDaysRemaining} days`
                              : "Expired"
                            : "N/A"}
                        </span>
                      ) : (
                        <span style={{ color: "#999" }}>-</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${student.status.toLowerCase()}`}
                      >
                        {student.status}
                      </span>
                    </td>
                    <td>
                      {student.licenseKey ? (
                        <LicenseManager
                          licenseKey={student.licenseKey}
                          studentName={student.name}
                          onStatusChange={async () => {
                            // Refresh student list after license status change
                            try {
                              const res = await studentAPI.getStudents({
                                all: true,
                              });
                              const data = Array.isArray(res?.data?.data)
                                ? res.data.data
                                : [];
                              const normalized = data.map((s) => {
                                const isActive = s.isActive !== false;
                                const isTrial = s.isTrialUser || false;
                                const raw =
                                  s.trialDaysRemaining !== undefined
                                    ? s.trialDaysRemaining
                                    : s.trialEndAt
                                    ? Math.max(
                                        0,
                                        Math.ceil(
                                          (new Date(s.trialEndAt) -
                                            new Date()) /
                                            (1000 * 60 * 60 * 24)
                                        )
                                      )
                                    : null;
                                const trialDaysRemaining = raw !== null ? Math.min(raw, 7) : null;
                                return {
                                  id: s.id || s._id,
                                  name: s.name || "",
                                  email: s.email || "",
                                  phone: s.phone || "",
                                  className: s.className || "",
                                  schoolName: s.schoolName || "",
                                  registeredDate: s.createdAt || "",
                                  isActive: isActive,
                                  status: computeStatus(isActive),
                                  password: s.initialPassword || "",
                                  licenseKey: s.licenseKey || null,
                                  registrationSource:
                                    s.registrationSource || "admin_panel",
                                  isTrialUser: isTrial,
                                  trialDaysRemaining: trialDaysRemaining,
                                  trialEndAt: s.trialEndAt || s.trialEndsAt || null,
                                  userType: s.userType || null,
                                  accessLevel: s.accessLevel || null,
                                };
                              });
                              const withPwds = mergeRecentPasswords(normalized);
                              setStudents(withPwds);
                              setFilteredStudents(withPwds);
                            } catch (e) {
                              console.error("Failed to refresh students", e);
                            }
                          }}
                        />
                      ) : (
                        <span
                          className="no-license"
                          style={{ color: "#999", fontStyle: "italic" }}
                        >
                          {student.userType === "trial_expired"
                            ? "Trial Expired"
                            : student.isTrialUser
                            ? "Trial User"
                            : "No License"}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {(student.isTrialUser ||
                          student.userType === "trial_expired") && (
                          <div className="extend-wrapper">
                            <button
                              type="button"
                              className="edit-button"
                              title="Extend Trial"
                              onClick={() => {
                                setExtendTrialStudent({
                                  id: student.id,
                                  name: student.name || student.email,
                                });
                                setExtendTrialDays(7);
                              }}
                              disabled={extendingId === student.id}
                            >
                              {extendingId === student.id ? "⏳" : "⏱"}
                            </button>
                            <span className="extend-label">Extend</span>
                          </div>
                        )}
                        <button
                          className={
                            student.isActive ? "edit-button" : "view-button"
                          }
                          title={
                            student.isActive
                              ? "Deactivate Student"
                              : "Activate Student"
                          }
                          onClick={() =>
                            handleToggleStatus(student.id, student.isActive)
                          }
                          disabled={togglingId === student.id}
                        >
                          {togglingId === student.id
                            ? "⏳"
                            : student.isActive
                            ? "⏸"
                            : "▶"}
                        </button>
                        <button
                          className="delete-button"
                          title="Remove Student"
                          onClick={() => handleDelete(student.id)}
                          disabled={deletingId === student.id}
                        >
                          <span className="minus">
                            {deletingId === student.id ? "…" : "-"}
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {!loading && filteredStudents.length > 0 && (
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1.5rem 1rem",
            borderTop: "2px solid #e0e0e0",
            backgroundColor: "#fafafa",
            flexWrap: "wrap",
            gap: "1rem"
          }}>
            {/* Items per page selector */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label style={{ fontSize: "0.9rem", fontWeight: "500", color: "#555" }}>
                Show:
              </label>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                style={{
                  padding: "0.5rem 1rem",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  fontSize: "0.9rem",
                  cursor: "pointer",
                  backgroundColor: "white"
                }}
              >
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
                <option value={500}>500 per page</option>
                <option value={1000}>1000 per page</option>
              </select>
              <span style={{ fontSize: "0.9rem", color: "#666", marginLeft: "1rem" }}>
                Showing{" "}
                {((currentPage - 1) * itemsPerPage) + 1} to{" "}
                {shouldLoadAll
                  ? Math.min(currentPage * itemsPerPage, filteredStudents.length)
                  : Math.min(currentPage * itemsPerPage, totalStudents)}{" "}
                of{" "}
                {shouldLoadAll ? filteredStudents.length : totalStudents}
                {selectedStudents.size > 0 && ` (${selectedStudents.size} selected)`}
              </span>
            </div>

            {/* Page navigation */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                ⏮ First
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                ◀ Prev
              </button>

              {/* Page numbers */}
              <div style={{ display: "flex", gap: "0.25rem" }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      style={{
                        padding: "0.5rem 0.75rem",
                        minWidth: "40px",
                        borderRadius: "6px",
                        border: currentPage === pageNum ? "2px solid #4CAF50" : "1px solid #ddd",
                        backgroundColor: currentPage === pageNum ? "#4CAF50" : "white",
                        color: currentPage === pageNum ? "white" : "#333",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: currentPage === pageNum ? "600" : "500",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== pageNum) {
                          e.target.style.backgroundColor = "#e8f5e9";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPage !== pageNum) {
                          e.target.style.backgroundColor = "white";
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  backgroundColor: currentPage === totalPages ? "#f5f5f5" : "white",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Next ▶
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  backgroundColor: currentPage === totalPages ? "#f5f5f5" : "white",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Last ⏭
              </button>
            </div>
          </div>
        )}

        {/* Extend Trial modal */}
        {extendTrialStudent && (
          <div
            className="extend-trial-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setExtendTrialStudent(null);
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "24px",
                borderRadius: "8px",
                minWidth: "320px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              }}
            >
              <h3 style={{ margin: "0 0 16px", fontSize: "18px" }}>
                Extend Trial
              </h3>
              <p style={{ margin: "0 0 12px", color: "#555" }}>
                Extend trial for <strong>{extendTrialStudent.name}</strong>
              </p>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Extend by (days):
              </label>
              <input
                type="number"
                min={1}
                max={365}
                value={extendTrialDays}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow empty field during typing
                  if (val === "") {
                    setExtendTrialDays("");
                  } else {
                    const num = parseInt(val, 10);
                    if (!isNaN(num)) setExtendTrialDays(num);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  marginBottom: "16px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              />
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setExtendTrialStudent(null)}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #ccc",
                    background: "#fff",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleExtendTrialConfirm}
                  disabled={!!extendingId}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    background: "#4a90e2",
                    color: "#fff",
                    borderRadius: "4px",
                    cursor: extendingId ? "not-allowed" : "pointer",
                  }}
                >
                  {extendingId ? "Extending…" : "Extend Trial"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribedStudents;
